import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import TerminalIcon from '@mui/icons-material/Terminal';
import api from '../../services/api';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const resp = await api.get('/audit-logs');
        setLogs(resp.data);
      } catch (e) {
        console.error('Failed to fetch logs:', e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, []);

  const [statusFilter, setStatusFilter] = useState('All');

  const filteredLogs = Array.isArray(logs) ? logs.filter(log => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      (log.initiator && log.initiator.toLowerCase().includes(term)) ||
      (log.action && log.action.toLowerCase().includes(term)) ||
      (log.details && log.details.toLowerCase().includes(term)) ||
      (log.ip && log.ip.toLowerCase().includes(term));
    const matchesStatus = statusFilter === 'All' || log.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) : [];

  return (
    <div className="animate-fade-in">
      <header style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 className="gradient-text" style={{ fontSize: '32px', marginBottom: '8px' }}>Security Audit Trail</h2>
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <SearchIcon style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '20px' }} />
            <input 
              type="text" 
              placeholder="Search by user, action, details, IP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '12px 12px 12px 40px',
                color: 'white',
                width: '300px',
                outline: 'none',
                transition: 'border-color 0.3s'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              padding: '12px 16px',
              color: 'white',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="All" style={{ background: '#1e293b' }}>All Statuses</option>
            <option value="success" style={{ background: '#1e293b' }}>Success</option>
            <option value="failed" style={{ background: '#1e293b' }}>Failed</option>
          </select>
        </div>
      </header>

      {loading ? (
        <div style={{ padding: '100px', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div className="animate-pulse">Retrieving encrypted audit records...</div>
        </div>
      ) : (
        <div className="card glass" style={{ padding: '0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.02)' }}>
                <th style={{ padding: '16px 24px', fontWeight: 600, color: 'white', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>Timestamp</th>
                <th style={{ padding: '16px 24px', fontWeight: 600, color: 'white', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>Initiator</th>
                <th style={{ padding: '16px 24px', fontWeight: 600, color: 'white', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>Operation</th>
                <th style={{ padding: '16px 24px', fontWeight: 600, color: 'white', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>Details</th>
                <th style={{ padding: '16px 24px', fontWeight: 600, color: 'white', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>IP Address</th>
                <th style={{ padding: '16px 24px', fontWeight: 600, color: 'white', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '20px 24px', fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)' }}>
                      <AccessTimeIcon style={{ fontSize: '16px' }} />
                      {log.timestamp}
                    </div>
                  </td>
                  <td style={{ padding: '20px 24px', fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(30, 64, 175, 0.2)', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PersonIcon style={{ fontSize: '16px' }} />
                      </div>
                      {log.initiator}
                    </div>
                  </td>
                  <td style={{ padding: '20px 24px', fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TerminalIcon style={{ fontSize: '16px', color: 'var(--text-muted)' }} />
                      {log.action}
                    </div>
                  </td>
                  <td style={{ padding: '20px 24px', fontSize: '13px', color: 'var(--text-muted)', maxWidth: '300px' }}>
                    {log.details || '—'}
                  </td>
                  <td style={{ padding: '20px 24px', fontSize: '13px', color: 'var(--text-muted)' }}>
                    {log.ip || '—'}
                  </td>
                  <td style={{ padding: '20px 24px' }}>
                    <span style={{ 
                      padding: '4px 12px', 
                      borderRadius: '20px', 
                      fontSize: '11px', 
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      background: log.status === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: log.status === 'success' ? '#10b981' : '#ef4444',
                      border: `1px solid ${log.status === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`
                    }}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
